import React from 'react';
import { useMobile } from '../../hooks/use-mobile';
import M from '../../images/icons/M.svg';
import N from '../../images/icons/N.svg';
import U from '../../images/icons/U.svg';
import {
  Box,
  Card as MuiCard,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  makeStyles,
  createStyles,
  Paper,
  Tooltip,
} from '@material-ui/core';
import { GetDistributorStatisticQueryResult } from '../../hooks/distributors/useGetDistributorStatistics';

interface Props {
  data: GetDistributorStatisticQueryResult[] | undefined;
  loading: boolean;
}

const getIconFromTitle = (title: string) => {
  const firstLetter = title.charAt(0).toLowerCase();
  const iconMap: { [key: string]: any } = {
    m: M,
    n: N,
    u: U,
  };
  return iconMap[firstLetter] || null;
};

const useStyles = makeStyles(theme =>
  createStyles({
    card: {
      border: '2px solid #fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },

    box: {
      display: 'flex',
      flexWrap: 'wrap',
      marginTop: '10 rem',
    },

    title: {
      fontFamily: 'Quicksand, sans-serif',
      fontSize: '2rem',
      color: '#1c2434',
      lineHeight: '2rem',
      fontWeight: 500,
    },
    typography: {
      backgroundColor: '#FF7206',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      padding: '10px',
      fontFamily: 'Quicksand, sans-serif',
      fontWeight: 600,
      color: '#fff',
      marginTop: '2rem',
      letterSpacing: '0.1rem',
    },
    paragraph: {
      fontFamily: 'Quicksand, sans-serif',
      fontWeight: 500,
      color: '#64748B',
      fontSize: '1rem',
      lineHeight: '1rem',

      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),
);

function formatCurrency(total: number | string): string {
  const numberValue = typeof total === 'string' ? parseFloat(total) : total;

  const formattedValue = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numberValue);

  return formattedValue;
}

const CardsAdmin: React.FC<Props> = ({ data, loading }: Props) => {
  const isMobile = useMobile();
  const classes = useStyles();

  return (
    <>
      {loading || !data ? (
        <CircularProgress />
      ) : (
        <>
          {data.length > 0 && (
            <Paper className={classes.typography}>
              <Typography>FATURAMENTO</Typography>
            </Paper>
          )}
          <Box display="flex" flexWrap="wrap" marginTop={2}>
            <Grid container spacing={3}>
              {data.map(distributor => (
                <Card
                  key={distributor.distributorId}
                  title={distributor.distributorName}
                  total={formatCurrency(distributor.totalRevenue)}
                  isMobile={isMobile}
                />
              ))}
            </Grid>
          </Box>
        </>
      )}
    </>
  );
};

interface CardProps {
  title: string;
  total: number | string;
  icon?: any;
  isMobile: boolean;
  data?: any;
}
function Card({ title, total, isMobile, data }: CardProps) {
  const icon = getIconFromTitle(title);
  const classes = useStyles();

  return (
    <Grid item xs={12} sm={6} md={4} lg={4}>
      <Box maxWidth="100%">
        <MuiCard className={classes.card}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  {icon && <img src={icon} alt={title} className="img-icons" />}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <Typography
                    className={classes.title}
                    variant={isMobile ? 'h5' : 'h3'}
                    component="h2"
                  >
                    {total}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <Tooltip title={title}>
                    <Typography
                      className={classes.paragraph}
                      variant={isMobile ? 'body2' : 'body1'}
                    >
                      {title}
                    </Typography>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </MuiCard>
      </Box>
    </Grid>
  );
}
export default CardsAdmin;
